/**

 * Modos de juego — slugs internos estables (API / socket / storage).

 * Nombres visibles para docentes: ver GAME_MODE_CATALOG.

 */

export type GameMode =
  | 'poll'
  | 'arena'
  | 'coop'
  | 'sandbox'
  | 'order'
  | 'point'
  | 'wall'
  | 'completar'
  | 'desarrollo'
  | 'vincula'
  | 'agrupa'
  | 'dictado'
  | 'anatomia'
  | 'rhythm_canvas'
  | 'melody_staff'
  | 'ear_training'
  | 'info_block'
  | 'canvas_breakdown'
  | 'symbol_analysis'
  | 'geometry_canvas'
  | 'cartesian_plane'
  | 'math_equation'
  | 'number_line'
  | 'counting_collection'
  | 'data_chart'
  | 'fraction_model'
  | 'percent_grid'
  | 'subtitulo'
  | 'destacado'
  | 'figura'
  | 'glosario_tabla'
  | 'formula_destacada'
  | 'structural_breakdown'
  | 'source_analysis';



/** @deprecated Slugs anteriores — se normalizan en runtime vía normalizeGameMode */

export type LegacyGameMode = 'sequencer' | 'pinpoint';



export const GAME_MODES: GameMode[] = [
  'poll',
  'arena',
  'coop',
  'sandbox',
  'order',
  'point',
  'wall',
  'completar',
  'desarrollo',
  'vincula',
  'agrupa',
  'dictado',
  'anatomia',
  'rhythm_canvas',
  'melody_staff',
  'ear_training',
  'info_block',
  'canvas_breakdown',
  'symbol_analysis',
  'geometry_canvas',
  'cartesian_plane',
  'math_equation',
  'number_line',
  'counting_collection',
  'data_chart',
  'fraction_model',
  'percent_grid',
  'subtitulo',
  'destacado',
  'figura',
  'glosario_tabla',
  'formula_destacada',
  'structural_breakdown',
  'source_analysis',
];



const LEGACY_MODE_MAP: Record<string, GameMode> = {

  sequencer: 'order',

  pinpoint: 'point',

};



/** Acepta slugs legacy (sequencer, pinpoint) y devuelve el slug canónico. */

export function normalizeGameMode(raw: unknown): GameMode | null {

  const key = String(raw ?? '').trim();

  if (!key) return null;

  if (GAME_MODES.includes(key as GameMode)) return key as GameMode;

  return LEGACY_MODE_MAP[key] ?? null;

}



export type SubjectTag =

  | 'general'

  | 'matematica'

  | 'lenguaje'

  | 'ciencias'

  | 'historia'

  | 'musica'

  | 'artes'

  | string;



export interface GameModeMeta {

  mode: GameMode;

  /** Nombre corto en UI (sidebar, tarjetas) */

  displayName: string;

  /** Subtítulo bajo el nombre */

  tagline: string;

  /** @deprecated Usar tagline — alias para compatibilidad */

  description: string;

  icon: string;

  supportsTeams: boolean;

  supportsIndividual: boolean;

}



export const GAME_MODE_CATALOG: Record<GameMode, GameModeMeta> = {

  poll: {

    mode: 'poll',

    displayName: 'Respuesta rápida',

    tagline: 'Pulsa, opina, ve',

    description: 'Pulsa, opina, ve',

    icon: '⚡',

    supportsTeams: false,

    supportsIndividual: true,

  },

  arena: {

    mode: 'arena',

    displayName: 'Cuestionario',

    tagline: 'Preguntas con tiempo y puntaje',

    description: 'Preguntas con tiempo y puntaje',

    icon: '📝',

    supportsTeams: true,

    supportsIndividual: true,

  },

  coop: {

    mode: 'coop',

    displayName: 'Tablero cooperativo',

    tagline: 'Turno, tablero, equipo',

    description: 'Turno, tablero, equipo',

    icon: '🎲',

    supportsTeams: true,

    supportsIndividual: true,

  },

  sandbox: {

    mode: 'sandbox',

    displayName: 'Explora',

    tagline: 'Mueve, simula, descubre',

    description: 'Mueve, simula, descubre',

    icon: '🔎',

    supportsTeams: true,

    supportsIndividual: false,

  },

  order: {

    mode: 'order',

    displayName: 'Secuencias',

    tagline: 'Secuencias y líneas de tiempo',

    description: 'Secuencias y líneas de tiempo',

    icon: '📅',

    supportsTeams: true,

    supportsIndividual: true,

  },

  point: {

    mode: 'point',

    displayName: 'Ubica',

    tagline: 'Ubica el punto, imagen o mapa',

    description: 'Ubica el punto, imagen o mapa',

    icon: '🎯',

    supportsTeams: false,

    supportsIndividual: true,

  },

  wall: {
    mode: 'wall',
    displayName: 'Debate',
    tagline: 'Opiniones en notas adhesivas',
    description: 'Opiniones en notas adhesivas',
    icon: '💬',
    supportsTeams: false,
    supportsIndividual: true,
  },
  completar: {
    mode: 'completar',
    displayName: 'Completar',
    tagline: 'Rellenar espacios en el texto',
    description: 'Cloze — arrastrar o teclear',
    icon: '✏️',
    supportsTeams: false,
    supportsIndividual: true,
  },
  desarrollo: {
    mode: 'desarrollo',
    displayName: 'Desarrollo',
    tagline: 'Respuesta abierta con rúbrica',
    description: 'Pensamiento crítico y redacción',
    icon: '📝',
    supportsTeams: false,
    supportsIndividual: true,
  },
  vincula: {
    mode: 'vincula',
    tagline: 'Empareja conceptos',
    displayName: 'Vincula',
    description: 'Dos columnas — une las parejas',
    icon: '🔗',
    supportsTeams: false,
    supportsIndividual: true,
  },
  agrupa: {
    mode: 'agrupa',
    displayName: 'Agrupa',
    tagline: 'Clasifica en categorías',
    description: 'Ordena conceptos en casilleros',
    icon: '🗂️',
    supportsTeams: false,
    supportsIndividual: true,
  },
  dictado: {
    mode: 'dictado',
    displayName: 'Dictado rítmico',
    tagline: 'Escucha y transcribe',
    description: 'Patrón auditivo con guía docente en impresión',
    icon: '👂',
    supportsTeams: false,
    supportsIndividual: true,
  },
  anatomia: {
    mode: 'anatomia',
    displayName: 'Anatomía rítmica',
    tagline: 'Duración de figuras',
    description: 'Valor matemático exacto de cada figura',
    icon: '🎼',
    supportsTeams: false,
    supportsIndividual: true,
  },
  rhythm_canvas: {
    mode: 'rhythm_canvas',
    displayName: 'Pista rítmica',
    tagline: 'Canvas visual de compás',
    description: 'Figuras rítmicas en pista — sin texto genérico',
    icon: '🥁',
    supportsTeams: false,
    supportsIndividual: true,
  },
  melody_staff: {
    mode: 'melody_staff',
    displayName: 'Pentagrama',
    tagline: 'Notas en el pentagrama',
    description: 'Identificación y lectura en clave de Sol',
    icon: '🎹',
    supportsTeams: false,
    supportsIndividual: true,
  },
  ear_training: {
    mode: 'ear_training',
    displayName: 'Entrenamiento auditivo',
    tagline: 'Escucha y analiza',
    description: 'Dictado con audio nativo y guía docente',
    icon: '👂',
    supportsTeams: false,
    supportsIndividual: true,
  },
  info_block: {
    mode: 'info_block',
    displayName: 'Bloque informativo',
    tagline: 'Contexto y enseñanza',
    description: 'Texto editorial con referencias visuales — sin evaluación',
    icon: '📖',
    supportsTeams: false,
    supportsIndividual: true,
  },
  canvas_breakdown: {
    mode: 'canvas_breakdown',
    displayName: 'Demostración guiada',
    tagline: 'Compás resuelto paso a paso',
    description: 'Andamiaje rítmico con explicación y pentagrama completo',
    icon: '🎓',
    supportsTeams: false,
    supportsIndividual: true,
  },
  symbol_analysis: {
    mode: 'symbol_analysis',
    displayName: 'Análisis de símbolos',
    tagline: 'Identifica ligaduras, puntillos y armadura',
    description: 'Pregunta dirigida sobre un símbolo real señalado en el pentagrama',
    icon: '🔍',
    supportsTeams: false,
    supportsIndividual: true,
  },
  geometry_canvas: {
    mode: 'geometry_canvas',
    displayName: 'Plano geométrico',
    tagline: 'Figuras con medidas reales',
    description: 'SVG vectorial de área, perímetro y diseño espacial',
    icon: '📐',
    supportsTeams: false,
    supportsIndividual: true,
  },
  cartesian_plane: {
    mode: 'cartesian_plane',
    displayName: 'Plano cartesiano',
    tagline: 'Coordenadas y transformaciones 2D',
    description: 'Ejes, puntos y polígonos en cuadrante I o plano completo',
    icon: '⊕',
    supportsTeams: false,
    supportsIndividual: true,
  },
  math_equation: {
    mode: 'math_equation',
    displayName: 'Motor de ecuaciones',
    tagline: 'Álgebra con huecos interactivos',
    description: 'Ecuaciones LaTeX con incógnitas y pasos de resolución',
    icon: '𝑥',
    supportsTeams: false,
    supportsIndividual: true,
  },
  number_line: {
    mode: 'number_line',
    displayName: 'Recta numérica',
    tagline: 'Secuencias con huecos guiados',
    description: 'Recta numérica SVG con números faltantes — el frontend renderiza el diseño',
    icon: '↔',
    supportsTeams: false,
    supportsIndividual: true,
  },
  counting_collection: {
    mode: 'counting_collection',
    displayName: 'Colección contable',
    tagline: 'Grupos pictóricos nativos',
    description: 'Grupos de ítems repetidos con íconos Serenity — la IA solo entrega counts e itemType',
    icon: '◫',
    supportsTeams: false,
    supportsIndividual: true,
  },
  data_chart: {
    mode: 'data_chart',
    displayName: 'Gráfico / Tabla',
    tagline: 'Estadística descriptiva visual',
    description: 'Barras, líneas, torta, puntos o tabla con datos numéricos estructurados',
    icon: '📊',
    supportsTeams: false,
    supportsIndividual: true,
  },
  fraction_model: {
    mode: 'fraction_model',
    displayName: 'Modelo de fracción',
    tagline: 'Partes de un todo en barra o pizza',
    description: 'Ilustración SVG de numerador/denominador — la IA solo entrega números limpios',
    icon: '½',
    supportsTeams: false,
    supportsIndividual: true,
  },
  percent_grid: {
    mode: 'percent_grid',
    displayName: 'Grilla de porcentaje',
    tagline: '10×10 para visualizar % exactos',
    description: 'Cuadrícula SVG con celdas coloreadas según el porcentaje indicado',
    icon: '%',
    supportsTeams: false,
    supportsIndividual: true,
  },
  subtitulo: {
    mode: 'subtitulo',
    displayName: 'Subtítulo de sección',
    tagline: 'Divide la lectura sin numerar preguntas',
    description: 'Encabezado editorial para apuntes y atlas',
    icon: '📑',
    supportsTeams: false,
    supportsIndividual: true,
  },
  destacado: {
    mode: 'destacado',
    displayName: 'Destacado',
    tagline: 'Sabías que… / dato clave',
    description: 'Caja de color para curiosidades y advertencias',
    icon: '💡',
    supportsTeams: false,
    supportsIndividual: true,
  },
  figura: {
    mode: 'figura',
    displayName: 'Recurso visual',
    tagline: 'Imagen sugerida con pie de foto',
    description: 'Placeholder elegante para insertar ilustración después',
    icon: '🖼️',
    supportsTeams: false,
    supportsIndividual: true,
  },
  glosario_tabla: {
    mode: 'glosario_tabla',
    displayName: 'Glosario',
    tagline: 'Concepto → definición',
    description: 'Tabla limpia de términos clave',
    icon: '📖',
    supportsTeams: false,
    supportsIndividual: true,
  },
  formula_destacada: {
    mode: 'formula_destacada',
    displayName: 'Fórmula destacada',
    tagline: 'Ecuación en LaTeX',
    description: 'Fórmula aislada con explicación de variables',
    icon: '∑',
    supportsTeams: false,
    supportsIndividual: true,
  },
  structural_breakdown: {
    mode: 'structural_breakdown',
    displayName: 'Desglose estructural',
    tagline: 'Verso, rima o sintaxis anotada',
    description: 'Líneas de texto con etiquetas métricas o gramaticales',
    icon: '📐',
    supportsTeams: false,
    supportsIndividual: true,
  },
  source_analysis: {
    mode: 'source_analysis',
    displayName: 'Análisis de fuente',
    tagline: 'Fuente primaria + preguntas abiertas',
    description: 'Cita histórica o literaria con inferencia y pensamiento crítico',
    icon: '📜',
    supportsTeams: false,
    supportsIndividual: true,
  },
};


